import { Link } from 'react-router-dom';
import MovieCard from '@/components/base/MovieCard';
import SectionHeading from '@/components/base/SectionHeading';
import CategoryShortcuts from '@/pages/home/components/CategoryShortcuts';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import SiteShell from '@/components/feature/SiteShell';
import { usePageMeta } from '@/hooks/usePageMeta';
import { newsArticles } from '@/mocks/news';
import {
  categoryList,
  hotMovies,
  inCategory,
  newUpdated,
  formatDate,
} from '@/lib/catalog';

export default function Home() {
  usePageMeta({
    title: '黄牛视频影视内容平台 - 最新电影电视剧资讯与播放',
    description:
      '黄牛视频为您提供丰富多样的电影、电视剧等影视内容，涵盖最新资讯和推荐，满足您多样化的观看需求。',
    keywords: '黄牛视频,影视内容,电影,电视剧,视频平台,影视资讯,在线观看,影视推荐',
  });

  const hot = hotMovies(12);
  const latest = newUpdated().slice(0, 6);
  const topNews = newsArticles.slice(0, 5);
  const tvPicks = inCategory('tv').slice(0, 6);
  const varietyPicks = inCategory('variety').slice(0, 6);

  return (
    <SiteShell>
      <HeroCarousel />

      <section className="w-full px-4 py-8 md:px-8 md:py-10">
        <div className="grid grid-cols-2 gap-3 border-b border-background-200 pb-8 sm:grid-cols-4 md:gap-6">
          {[
            { value: '24', unit: '部', label: '精选影视条目' },
            { value: '10', unit: '篇', label: '影视资讯报道' },
            { value: '6', unit: '类', label: '多维筛选条件' },
            { value: '100%', unit: '', label: '移动端适配' },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col gap-1">
              <p className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
                {stat.value}
                <span className="ml-0.5 font-label text-xs font-medium text-foreground-400">
                  {stat.unit}
                </span>
              </p>
              <p className="text-xs text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 pb-10 md:px-8 md:pb-12">
        <SectionHeading
          title="分类快捷通道"
          en="Browse"
          subtitle="按内容类型直接进入频道，再用类型、地区与年份筛出你真正想看的作品。"
        />
        <CategoryShortcuts />
      </section>

      <section className="w-full bg-background-100/70 px-4 py-10 md:px-8 md:py-12">
        <SectionHeading
          title="热门影视推荐"
          en="Trending now"
          subtitle="按播放热度综合排序，数据为示例内容，用于展示推荐版式。"
          linkLabel="查看全部作品"
          linkTo="/category/movie"
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-6">
          {hot.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div>
            <SectionHeading
              title="最近更新"
              en="Newly updated"
              subtitle="按更新时间排列，方便跟进新上线的影视作品。"
              linkLabel="进入电影频道"
              linkTo="/category/movie"
            />
            <ul className="divide-y divide-background-200 border-y border-background-200">
              {latest.map((movie, index) => (
                <li key={movie.id}>
                  <Link
                    to={`/detail/${movie.id}`}
                    className="group flex items-center gap-3 py-3 transition-colors hover:bg-background-100/60 md:gap-4 md:py-3.5"
                  >
                    <span className="w-6 shrink-0 text-center font-label text-sm font-bold text-foreground-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="h-16 w-11 shrink-0 overflow-hidden rounded bg-background-200 md:h-20 md:w-14">
                      <img
                        src={movie.poster}
                        alt={`${movie.title} 封面`}
                        title={`${movie.title} - 最新更新 - 黄牛视频`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-700 md:text-[15px]">
                        {movie.title}
                      </h3>
                      <p className="mt-1 truncate text-xs text-foreground-500">
                        {movie.year} · {movie.genres.join(' / ')} · {movie.region}
                      </p>
                      <p className="mt-1 hidden truncate text-xs text-foreground-400 sm:block">
                        {movie.tagline}
                      </p>
                    </div>
                    <div className="hidden shrink-0 text-right sm:block">
                      <p className="font-label text-xs font-semibold text-accent-600">
                        {movie.rating.toFixed(1)}
                      </p>
                      <p className="mt-1 text-[11px] text-foreground-400">
                        {movie.updatedAt.slice(5).replace('-', '/')} 更新
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100/60">
              <div className="flex items-center justify-between bg-primary-900 px-4 py-3">
                <h2 className="font-heading text-sm font-bold text-background-50">影视资讯精选</h2>
                <Link to="/news" className="text-[11px] text-primary-200 hover:text-accent-300">
                  更多资讯
                </Link>
              </div>
              <ul className="divide-y divide-background-200">
                {topNews.map((article) => (
                  <li key={article.id}>
                    <Link
                      to={`/news/${article.id}`}
                      className="group block px-4 py-3 transition-colors hover:bg-background-50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="rounded-sm bg-accent-100 px-1.5 py-0.5 text-[10px] font-medium text-accent-800">
                          {article.category}
                        </span>
                        <span className="text-[11px] text-foreground-400">
                          {article.publishedAt}
                        </span>
                      </div>
                      <p className="mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-foreground-800 transition-colors group-hover:text-primary-700">
                        {article.title}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 rounded-lg bg-secondary-50 p-4">
              <p className="font-heading text-sm font-bold text-secondary-900">本周排片提醒</p>
              <p className="mt-2 text-xs leading-relaxed text-secondary-800/80">
                《长夜将明》10 月 24 日上映，《雪山信使》修复版正在重映。影迷可先收藏详情页，上映后再回来查看。
              </p>
              <Link
                to="/detail/m01"
                className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-secondary-900 underline decoration-secondary-300 underline-offset-4"
              >
                查看《长夜将明》详情
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full bg-background-100/70 px-4 py-10 md:px-8 md:py-12">
        <SectionHeading
          title="剧集精挑"
          en="Series picks"
          subtitle="古装、悬疑、都市、青春，近期讨论度较高的剧集。"
          linkLabel="进入电视剧频道"
          linkTo="/category/tv"
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-6">
          {tvPicks.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      <section className="w-full px-4 py-10 md:px-8 md:py-12">
        <SectionHeading
          title="综艺热播"
          en="Variety shows"
          subtitle="音乐、推理、喜剧与生活纪实，适合下饭与周末。"
          linkLabel="进入综艺频道"
          linkTo="/category/variety"
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-6">
          {varietyPicks.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      <section className="w-full px-4 pb-6 md:px-8">
        <div className="relative overflow-hidden rounded-lg bg-primary-900 px-5 py-8 md:px-10 md:py-12">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-accent-500/15 blur-2xl" />
          <div className="relative grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="font-label text-[10px] uppercase tracking-[0.24em] text-accent-400">
                About Huangniu TV
              </span>
              <h2 className="mt-3 font-heading text-2xl font-extrabold leading-tight text-background-50 md:text-3xl">
                一个把影视资讯讲清楚的平台
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-primary-200">
                黄牛视频专注电影、电视剧与综艺内容。我们不做夸张的标题党，只把作品信息、更新时间与主创资料整理清楚，让你在几分钟内决定要不要看。
              </p>
              <Link
                to="/about"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-900 transition-colors hover:bg-accent-100"
              >
                了解黄牛视频
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { icon: 'ri-list-check-2', title: '多维筛选', desc: '类型、地区、年份组合筛选' },
                { icon: 'ri-newspaper-line', title: '资讯同步', desc: '每日更新的影视动态' },
                { icon: 'ri-play-circle-line', title: '播放演示', desc: '前端控件交互示范' },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-background-50/15 bg-background-50/5 p-3.5"
                >
                  <i className={`${item.icon} text-lg text-accent-400`} />
                  <p className="mt-2 font-heading text-sm font-bold text-background-50">
                    {item.title}
                  </p>
                  <p className="mt-1 text-[11px] leading-relaxed text-primary-300">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 pb-4 md:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100/60 px-4 py-4">
          <p className="text-xs text-foreground-500">
            内容分类：{categoryList.map((item) => item.label).join(' · ')} ｜ 最近更新：
            {formatDate(latest[0].updatedAt)}
          </p>
          <Link
            to="/search"
            className="flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-accent-600"
          >
            <i className="ri-search-line" />
            去搜索想看的作品
          </Link>
        </div>
      </section>
    </SiteShell>
  );
}